import { cn } from "@/lib/cn";
import { Floating, Icon } from "@/shared";
import Tooltip from "@components/ui/tooltip";
import { trackContent } from "@editor/utils";
import { useAppDispatch } from "@store";
import { setDialogId } from "@store/global-slice";
import { IconMessages } from "@tabler/icons-react";
import { useEffect, useState } from "react";
import { CommentsCountProvider, FilterProvider } from "../../providers";
import { Filter } from "./Filter";
import { ForMeFilter } from "./ForMeFilter";

import { List } from "./List";

const GHOST_BUTTON_STYLE =
  "tw-flex tw-size-8 tw-cursor-pointer tw-items-center tw-justify-center tw-rounded-md tw-border tw-border-solid tw-border-transparent tw-bg-transparent tw-transition-colors tw-duration-200 hover:tw-bg-[#F3F2F5]";

export const Threads = () => {
  "use no memo";
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const dispatch = useAppDispatch();

  useEffect(() => {
    const url = new URL(window.location.href);
    if (url.searchParams.has("comments")) {
      setIsOpen(true);
      url.searchParams.delete("comments");
      window.history.replaceState({}, "", url.toString());
    }
  }, [dispatch]);

  return (
    <Floating
      isOpen={isOpen}
      setIsOpen={setIsOpen}
      isPortal
      overwriteIndex={10000}
      placement="bottom-start"
      flipOptions={{ fallbackStrategy: "initialPlacement" }}
      disableAutoUpdate
      className="!tw-left-auto !tw-right-2 !tw-top-12 !tw-translate-x-0"
      trigger={
        <CommentsCountProvider>
          <Tooltip placement="bottom">
            <Tooltip.Trigger asChild>
              <div
                className={cn(GHOST_BUTTON_STYLE, {
                  "tw-bg-[#F3F2F5]": isOpen,
                })}
              >
                <IconMessages size={20} stroke={1.5} className="tw-fill-none tw-text-[#12033E]" />
              </div>
            </Tooltip.Trigger>
            <Tooltip.Content>
              <Tooltip.Text>View and add comments</Tooltip.Text>
            </Tooltip.Content>
          </Tooltip>
        </CommentsCountProvider>
      }
    >
      <div className="tw-relative tw-h-[calc(100vh_-_56px)] tw-min-h-24 tw-w-[400px] tw-overflow-hidden tw-rounded-lg tw-border tw-border-solid tw-border-blue-100 tw-bg-[#FDFCFD] tw-shadow-comment-popover">
        <FilterProvider>
          <Header setIsOpen={setIsOpen} />
          <List />
        </FilterProvider>
      </div>
    </Floating>
  );
};

const Header = ({ setIsOpen }: { setIsOpen: (value: boolean) => void }) => {
  const dispatch = useAppDispatch();
  return (
    <div className="tw-absolute tw-left-0 tw-top-0 tw-z-10 tw-mb-4 tw-w-full tw-pb-2 tw-pt-7 tw-backdrop-blur-[8px]">
      <div className="tw-flex tw-items-center tw-justify-between tw-px-6 tw-pb-2">
        <div className="tw-flex tw-items-center tw-font-sans tw-text-[13px]/tight tw-font-semibold tw-text-blue-base">
          <IconMessages
            size={20}
            stroke={1.5}
            className="tw-mr-2 tw-fill-none tw-text-[#88819F]"
          />
          Comments
        </div>
        <div className="tw-flex tw-items-center tw-gap-1">
          <Filter iconStyle={GHOST_BUTTON_STYLE} />
          <div
            role="button"
            tabIndex={0}
            className={GHOST_BUTTON_STYLE}
            onClick={() => {
              dispatch(setDialogId({ id: "QdziCeYhAKE11aW0yj5cP" }));
              setIsOpen(false);
              trackContent("click update comments notification settings");
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                dispatch(setDialogId({ id: "QdziCeYhAKE11aW0yj5cP" }));
                setIsOpen(false);
                trackContent("click update comments notification settings");
              }
            }}
          >
            <Icon icon="ri-notification-line" color="#88819F" />
          </div>
          <div
            role="button"
            tabIndex={0}
            className={GHOST_BUTTON_STYLE}
            onClick={() => setIsOpen(false)}
            onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') setIsOpen(false); }}
          >
            <Icon icon="ri-close-fill" color="#88819F" />
          </div>
        </div>
      </div>
      <ForMeFilter />
    </div>
  );
};
